import React, { useState } from 'react';
import { cashflowApi } from '../services/api';
import '../styles/Modal.css';

function SendToFinalyzerModal({ entity, periodLabel, timestamp, onClose, onComplete }) {
  const [toDate, setToDate] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Extract end date from period label for default value
  const extractEndDate = () => {
    if (!periodLabel) return '';
    const parts = periodLabel.split('_to_');
    if (parts.length === 2) {
      return parts[1];
    }
    return '';
  };

  // Format date to DD-MMM-YYYY for Finalyzer
  const formatDateForFinalyzer = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  };

  // Set default to_date on mount
  React.useEffect(() => {
    const endDate = extractEndDate();
    if (endDate) {
      setToDate(formatDateForFinalyzer(endDate));
    }
  }, [periodLabel]);

  const handleSend = async () => {
    if (!toDate) {
      setError('Please enter a valid to_date for Finalyzer');
      return;
    }

    setSending(true);
    setError(null);
    setResult(null);

    try {
      const data = await cashflowApi.sendToFinalyzer(
        entity.name,
        periodLabel,
        toDate,
        timestamp
      );
      setResult(data);
    } catch (err) {
      console.error('Error sending to Finalyzer:', err);
      setError(err.message || 'Failed to send cashflow to Finalyzer');
    } finally {
      setSending(false);
    }
  };

  const formatPeriodLabel = (label) => {
    if (!label) return 'N/A';
    const parts = label.split('_to_');
    if (parts.length === 2) {
      return `${parts[0]} to ${parts[1]}`;
    }
    return label;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Send to Finalyzer</h2>
          <button className="modal-close" onClick={onClose} disabled={sending}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {!result && (
            <>
              <div className="alert alert-info" style={{ fontSize: '14px' }}>
                <strong>About Finalyzer Integration:</strong>
                <br />
                This will send the cashflow data to Finalyzer using the <code>postTrialBalance</code> endpoint with scenario "Cashflow" and data_mnemonic "SAP-Cashflow Non TB".
                <br />
                <br />
                The cashflow line items will be mapped to Finalyzer GL codes according to the COA mapping configuration.
              </div>

              <div className="info-card" style={{ marginBottom: '20px' }}>
                <p><strong>Entity:</strong> {entity.name}</p>
                <p><strong>Period:</strong> {formatPeriodLabel(periodLabel)}</p>
                <p><strong>Timestamp:</strong> {timestamp}</p>
              </div>

              <div className="form-group">
                <label>
                  Finalyzer To Date:
                  <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                    Format: DD-MMM-YYYY (e.g., 31-Dec-2025)
                  </small>
                </label>
                <input
                  type="text"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={sending}
                  className="form-control"
                  placeholder="31-Dec-2025"
                />
              </div>

              {error && (
                <div className="alert alert-danger">
                  <strong>Error:</strong> {error}
                </div>
              )}
            </>
          )}

          {result && (() => {
            const fzData = result.finalyzer_response?.data || {};
            const hasWarnings = parseInt(fzData.warning || 0) > 0;
            const hasFailed   = parseInt(fzData.failed  || 0) > 0;
            const absentInAM  = parseInt(fzData.absentinAM || 0) > 0;
            return (
              <>
                <div className="alert alert-success">
                  <strong>✓ Successfully Sent to Finalyzer!</strong>
                  <ul style={{ marginTop: '10px', marginLeft: '20px', lineHeight: '1.8' }}>
                    <li><strong>Line Items Sent:</strong> {result.line_items_sent}</li>
                    <li><strong>Taxonomy:</strong> IndAS — IndAS entities</li>
                    <li><strong>Accepted by Finalyzer:</strong> {fzData.success || result.line_items_sent} of {fzData.total || result.line_items_sent}</li>
                    {hasFailed   && <li style={{ color: '#b94a48' }}><strong>Failed:</strong> {fzData.failed}</li>}
                    {hasWarnings && <li style={{ color: '#8a6d3b' }}><strong>Warnings:</strong> {fzData.warning}</li>}
                  </ul>
                </div>

                {fzData.book_ids && (
                  <div className="alert alert-info" style={{ marginTop: '8px', fontSize: '13px' }}>
                    <strong>📖 Where to view in Finalyzer:</strong>
                    <ul style={{ marginTop: '8px', marginLeft: '20px', lineHeight: '1.8' }}>
                      <li><strong>Book ID:</strong> {fzData.book_ids}</li>
                      <li><strong>Period:</strong> {fzData.book_period_ids}</li>
                      <li><strong>Scenario:</strong> Cashflow &nbsp;|&nbsp; <strong>Data Mnemonic:</strong> SAP-Cashflow Non TB</li>
                    </ul>
                    <p style={{ marginTop: '8px', marginBottom: 0, color: '#31708f' }}>
                      In Finalyzer portal, navigate to the <strong>IndAS taxonomy book {fzData.book_ids}</strong> for this entity.
                    </p>
                  </div>
                )}

                <div className="alert alert-warning" style={{ marginTop: '8px', fontSize: '13px', borderLeft: '4px solid #e6a817' }}>
                  <strong>⚠ If you see "Object not found" in Finalyzer portal:</strong>
                  <p style={{ marginTop: '6px', marginBottom: '4px' }}>
                    This is a <strong>Finalyzer-side configuration issue</strong> — the data was pushed successfully from our system
                    (response from Finalyzer: <em>status: Success, {fzData.success || result.line_items_sent} records accepted</em>).
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    The entity is not yet provisioned in the <strong>IndAS taxonomy book{fzData.book_ids ? ` (Book ID: ${fzData.book_ids})` : ''}</strong> on Finalyzer's side.
                    Please contact the <strong>Finalyzer team</strong> to set up this entity in the IndAS book.
                  </p>
                </div>

                {absentInAM && (
                  <div className="alert alert-warning" style={{ marginTop: '8px', fontSize: '13px' }}>
                    <strong>⚠ Finalyzer Warning:</strong> {fzData.absentinAM} GL account(s) were not found in Finalyzer's account master.
                    These lines were accepted but may not map to the correct accounts. Verify the COA mapping with the Finalyzer team.
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div className="modal-footer">
          {result ? (
            <button onClick={() => onComplete(result)} className="btn btn-secondary">
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleSend}
                disabled={sending || !toDate}
                className="btn btn-success"
              >
                {sending ? 'Sending...' : 'Send to Finalyzer'}
              </button>
              <button onClick={onClose} disabled={sending} className="btn btn-secondary">
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SendToFinalyzerModal;
